import { DeploymentRing, Environment } from '@fluidx/office-fluid-types';
import { getEnvironmentConfigFromRing, getRingForEnvironment } from '@fluidx/utilities';
import { getCachedPrefetchEntryForDeploymentRing, cachePrefetchEntryForDeploymentRing } from './ManifestCacheHelpers';
import { getCachedOdspDriverManifest, fetchOdspDriverManifest } from './OdspDriverManifestAccessors';
import { currentVersionKey } from './OdspDriverManifestType';

export interface OdspDriverPrecacheConfig {
  /**
   * The environment for the driver, defaults to production
   */
  environment?: Environment;
}

/**
 * Pre-fetches the ODSP driver files into the browser cache, to gain a performance boost.
 * This function looks fetches the latest ODSP deployment manifest and JS files,
 * or no-ops if these files are already in the cache.
 */
export async function precacheDynamicOdspDriver(config: OdspDriverPrecacheConfig = {}): Promise<void> {
  const deploymentRing = getRingForEnvironment(config.environment);
  const manifest = getCachedOdspDriverManifest(deploymentRing) || (await fetchOdspDriverManifest(deploymentRing));
  const currentVersionEntry = manifest[currentVersionKey];

  const prefetchCacheEntry = getCachedPrefetchEntryForDeploymentRing(deploymentRing);
  // compare index file names and do a prefetch only if there is a mismatch
  if (!prefetchCacheEntry || prefetchCacheEntry !== currentVersionEntry.index) {
    const precacheFileLinks = [currentVersionEntry.index, ...(currentVersionEntry.additionalFiles || [])].map(
      (filename) => manifest.jsBaseUrl + '/' + filename
    );
    // Using a document fragment here to reduce the number of writes to the dom
    const fragment = document.createDocumentFragment();
    precacheFileLinks.forEach((urlToPrecache) => {
      const prefetchTag = document.createElement('link');
      prefetchTag.rel = 'prefetch';
      prefetchTag.href = urlToPrecache;
      prefetchTag.as = 'script';
      prefetchTag.crossOrigin = 'anonymous';
      fragment.appendChild(prefetchTag);
    });
    document.head.appendChild(fragment);
    cachePrefetchEntryForDeploymentRing(currentVersionEntry.index, deploymentRing);
  }
}

/**
 * @deprecated [Please use "precacheDynamicOdspDriver" instead. In the future, this API will be removed in favor of the new one]
 *
 * Pre-fetches the ODSP driver files into the browser cache, to gain a performance boost.
 * This function looks fetches the latest ODSP deployment manifest and JS files,
 * or no-ops if these files are already in the cache.
 */
export async function prefetchDynamicOdspDriver(environment?: DeploymentRing): Promise<void> {
  return precacheDynamicOdspDriver({ environment: getEnvironmentConfigFromRing(environment) });
}
